import React, { ReactElement } from "react";
import './TutorialModal.scss';

interface TutorialModalProps {
    setShowTutorial: React.Dispatch<React.SetStateAction<boolean>>;
}

const TutorialModal = ({
    setShowTutorial }: TutorialModalProps): ReactElement => {

    const onSkipClick = () => {
        setShowTutorial(false);
    }

    const onSuppressClick = (e: React.ChangeEvent<HTMLInputElement>) => {
        localStorage.setItem('suppressNewProjectWizardTaskListTutorial', e.target.checked.toString());
    }

    return (
        <div className="show-modal modal fade show" id="newProjectWizardTutorialModal" tabIndex={-1} role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-lg" role="document">

                <div className="modal-content">
                    <div className="modal-body">

                        <div id="tutorialCarousel" className="carousel slide" data-interval="false">

                            <ol className="carousel-indicators">
                                <li data-target="#tutorialCarousel" data-slide-to="0" className="active"></li>
                                <li data-target="#tutorialCarousel" data-slide-to="1"></li>
                                <li data-target="#tutorialCarousel" data-slide-to="2"></li>
                                <li data-target="#tutorialCarousel" data-slide-to="3"></li>
                            </ol>

                            <div className="carousel-inner h-100">
                                <div className="carousel-item active align-self-center">
                                    <div className="container-fluid">
                                        <div className="row mb-3">
                                            <div className="col-md-4 mx-auto text-center"><i className="fa-regular fa-thumbs-up fa-4x"></i></div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-9 mx-auto text-center">
                                                <div className="slide-heading mb-1">Looking Good!</div>
                                                <div className="slide-one-text">Next, let's go over some important information so you can get the most out of Pacer's functionality.</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="carousel-item align-self-center">
                                    <div className="container-fluid">
                                        <div className="row mb-3">
                                            <div className="col-md-10 mx-auto">
                                                <div className="slide-heading mb-4">
                                                    1. What is a Task List and how does it work?
                                                </div>
                                                <div className="slide-subheading mb-2">
                                                    What is a Task List?
                                                </div>
                                                <div className="slide-text mb-4">
                                                    A Task List is a set of tasks within a project that can be applied to multiple locations.
                                                </div>
                                                <div className="slide-subheading mb-2">
                                                    How do Task Lists work?
                                                </div>
                                                <div className="slide-text">
                                                    Task Lists act as a source of truth, allowing you to make changes that will update to all locations within your project, in turn streamlining your work process!
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="carousel-item align-self-center">
                                    <div className="container-fluid">
                                        <div className="row mb-3">
                                            <div className="col-md-10 mx-auto">
                                                <div className="slide-heading mb-4">
                                                    2. Decide how to structure your Task List
                                                </div>
                                                <div className="slide-heading-text mb-4">
                                                    When creating a task list, you will need to define the identifying factor that will tell Pacer how to display progress information post project publication.
                                                </div>
                                                <div className="slide-subheading mb-2">
                                                    Relative Date <span className="badge pacer61-badge ms-2"><i className="fa-regular fa-sparkles"></i>Pacer Recommended</span>
                                                </div>
                                                <div className="slide-text mb-3">
                                                    Define the timeframe for task completion before project launch. Based on this setting, Pacer will automatically generate task due dates relative to the project completion date. This completion date is set up in the final step!
                                                </div>
                                                <div className="mb-4 d-flex">
                                                    <div className="px-2">
                                                        <i className="fa-regular fa-lightbulb"></i>
                                                    </div>
                                                    <div className="flex-grow-1 pl-1 slide-text">
                                                        By default, tasks will use a Relative Date
                                                    </div>
                                                </div>
                                                <div className="slide-subheading mb-2">
                                                    Static Date
                                                </div>
                                                <div className="slide-text mb-3">
                                                    Add in exact dates for when each tasks should be complete.
                                                </div>
                                                <div className="d-flex">
                                                    <div className="px-2">
                                                        <i className="fa-regular fa-lightbulb"></i>
                                                    </div>
                                                    <div className="flex-grow-1 pl-1 slide-text">
                                                        This method is not recommended if you plan on having multiple locations within your project with varying completion dates.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="carousel-item align-self-center">
                                    <div className="container-fluid">
                                        <div className="row mb-3">
                                            <div className="col-md-10 mx-auto">
                                                <div className="slide-heading mb-4">
                                                    3. How to add Task Lists
                                                </div>
                                                <div className="slide-heading-text mb-4">
                                                    We offer multiple ways for you to populate your project:
                                                </div>
                                                <div className="slide-subheading mb-2">
                                                    Copying an existing Task List <span className="badge pacer61-badge ms-2"><i className="fa-regular fa-sparkles"></i>Pacer Recommended</span>
                                                </div>
                                                <div className="slide-text mb-3">
                                                    Copying existing Task Lists from previous projects will streamline your workflow. You can make changes to these tasks within the project.
                                                </div>
                                                <div className="mb-4 d-flex">
                                                    <div className="px-2">
                                                        <i className="fa-regular fa-lightbulb"></i>
                                                    </div>
                                                    <div className="flex-grow-1 pl-1 slide-text">
                                                        Changes made to a copied Task Lists will not impact the original source!
                                                    </div>
                                                </div>
                                                <div className="slide-subheading mb-2">
                                                    Upload a Task List
                                                </div>
                                                <div className="slide-text mb-3">
                                                    Pacer will provide an Excel Sheet template so you can easily fill out your tasks and populate your Task List with a single upload.
                                                </div>
                                                <div className="slide-subheading mb-2">
                                                    Manually create a Task List
                                                </div>
                                                <div className="slide-text mb-3">
                                                    Create your Task List by adding details and adding tasks individually.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button className="carousel-control-prev" type="button" data-target="#tutorialCarousel" data-slide="prev">
                                {/* <Icon name="chevron-left" stylePrefix="fas fa-lg" /> */}
                                <i className="fa-regular fa-chevron-left fa-lg"></i>
                                <span className="sr-only">Previous</span>
                            </button>
                            <button className="carousel-control-next" type="button" data-target="#tutorialCarousel" data-slide="next">
                                <i className="fa-regular fa-chevron-right fa-lg"></i>
                                <span className="sr-only">Next</span>
                            </button>
                        </div>

                    </div>
                    <div className="modal-footer">

                        <div>
                            <label htmlFor="suppressTutorialCheck">
                                <input type="checkbox" name="suppressTutorialCheck" id="suppressTutorialCheck" onChange={onSuppressClick} /> {`Don't show this message again`}
                            </label>
                        </div>
                        <button type="button" className="btn btn-primary" data-dismiss="modal" onClick={onSkipClick}>Skip Tutorial</button>

                    </div>
                </div>

            </div>
        </div>
    )
};

export default TutorialModal;
